import { motion } from "framer-motion";
import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";

const Education = () => {
	const { t } = useLanguage();

	// Project data linked to career plan - each project should total 10 hours with all 6 projects totaling 60 hours
	const projectData = [
		{
			id: 1,
			title: t("educationProjects.technicalSupport.title"),
			realHours: "632",
			validatedHours: "10",
			skills: t("educationProjects.technicalSupport.skills"),
			careerBenefit: t("educationProjects.technicalSupport.careerBenefit"),
			description: t("educationProjects.technicalSupport.description"),
			link: "/projects/technical-support",
		},
		{
			id: 2,
			title: t("educationProjects.picoCTF.title"),
			realHours: "24",
			validatedHours: "10",
			skills: t("educationProjects.picoCTF.skills"),
			careerBenefit: t("educationProjects.picoCTF.careerBenefit"),
			description: t("educationProjects.picoCTF.description"),
			link: "/projects/picoctf-2023",
		},
		{
			id: 3,
			title: t("educationProjects.stock4me.title"),
			realHours: "0",
			validatedHours: "10",
			skills: t("educationProjects.stock4me.skills"),
			careerBenefit: t("educationProjects.stock4me.careerBenefit"),
			description: t("educationProjects.stock4me.description"),
			link: "/projects/stock4me",
		},
		{
			id: 4,
			title: t("educationProjects.cyberConference.title"),
			realHours: "1.5",
			validatedHours: "1.5",
			skills: t("educationProjects.cyberConference.skills"),
			careerBenefit: t("educationProjects.cyberConference.careerBenefit"),
			description: t("educationProjects.cyberConference.description"),
			link: "/projects/cybersecurity-conference",
		},
		{
			id: 5,
			title: t("educationProjects.odooVisit.title"),
			realHours: "8",
			validatedHours: "8",
			skills: t("educationProjects.odooVisit.skills"),
			careerBenefit: t("educationProjects.odooVisit.careerBenefit"),
			description: t("educationProjects.odooVisit.description"),
			link: "/projects/odoo-visit",
		},
		{
			id: 6,
			title: t("educationProjects.ethicalHacking.title"),
			realHours: "14.5",
			validatedHours: "10",
			skills: t("educationProjects.ethicalHacking.skills"),
			careerBenefit: t("educationProjects.ethicalHacking.careerBenefit"),
			description: t("educationProjects.ethicalHacking.description"),
			link: "/projects/ethical-hacking-course",
		},
		{
			id: 7,
			title: t("educationProjects.sushiShop.title"),
			realHours: "239",
			validatedHours: "10",
			skills: t("educationProjects.sushiShop.skills"),
			careerBenefit: t("educationProjects.sushiShop.careerBenefit"),
			description: t("educationProjects.sushiShop.description"),
			link: "/projects/sushi-shop",
		},
		{
			id: 8,
			title: t("educationProjects.axaHackathon.title"),
			realHours: "8",
			validatedHours: "0",
			skills: t("educationProjects.axaHackathon.skills"),
			careerBenefit: t("educationProjects.axaHackathon.careerBenefit"),
			description: t("educationProjects.axaHackathon.description"),
			link: "/projects/axa-hackathon",
		},
		{
			id: 9,
			title: t("educationProjects.spar.title"),
			realHours: "128",
			validatedHours: "0",
			skills: t("educationProjects.spar.skills"),
			careerBenefit: t("educationProjects.spar.careerBenefit"),
			description: t("educationProjects.spar.description"),
			link: "/projects/spar-experience",
		},
		{
			id: 10,
			title: t("educationProjects.axaStudentJob.title"),
			realHours: "242",
			validatedHours: "0",
			skills: t("educationProjects.axaStudentJob.skills"),
			careerBenefit: t("educationProjects.axaStudentJob.careerBenefit"),
			description: t("educationProjects.axaStudentJob.description"),
			link: "/projects/axa-student-job",
		},
	];

	// Animation variants
	const containerVariants = {
		hidden: { opacity: 0 },
		visible: {
			opacity: 1,
			transition: {
				staggerChildren: 0.2,
			},
		},
	};

	const itemVariants = {
		hidden: { opacity: 0, y: 20 },
		visible: {
			opacity: 1,
			y: 0,
			transition: { duration: 0.5 },
		},
	};

	return (
		<section
			id="education"
			className="py-16 md:py-20 bg-gray-100 dark:bg-gray-800 transition-colors duration-300">
			<div className="container mx-auto px-4">
				<motion.div
					className="text-center mb-10 md:mb-16"
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true }}
					transition={{ duration: 0.5 }}>
					<h2 className="text-2xl md:text-3xl font-bold mb-3 md:mb-4 dark:text-white">
						{t("education")}
					</h2>
					
				</motion.div>

				

				

				<div>
					<motion.h3
						className="text-xl md:text-2xl font-semibold mb-4 md:mb-6 dark:text-white text-center md:text-left"
						initial={{ opacity: 0, y: 10 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true }}
						transition={{ duration: 0.5, delay: 0.4 }}>
						{t("projects")}
					</motion.h3>

					{/* Desktop view (table) */}
					<div className="hidden md:block bg-white dark:bg-gray-700 rounded-lg shadow-md overflow-hidden">
						<div className="overflow-x-auto">
							<table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
								<thead className="bg-gray-50 dark:bg-gray-800">
									<tr>
										<th
											scope="col"
											className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
											{t("educationTableHeaders.projectName")}
										</th>
										<th
											scope="col"
											className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
											{t("educationTableHeaders.realHours")}
										</th>
										<th
											scope="col"
											className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
											{t("educationTableHeaders.validatedHours")}
										</th>
										<th
											scope="col"
											className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
											{t("educationTableHeaders.theme")}
										</th>
										<th
											scope="col"
											className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
											{t("educationTableHeaders.details")}
										</th>
									</tr>
								</thead>
								<tbody className="bg-white dark:bg-gray-700 divide-y divide-gray-200 dark:divide-gray-600">
									{projectData.map((project) => (
										<tr key={project.id}>
											<td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
												{project.title}
											</td>
											<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
												{project.realHours}h
											</td>
											<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
												{project.validatedHours}h
											</td>
											<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
												{project.skills.split(',')[0]}
											</td>
											<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
												<Link
													to={project.link}
													className="text-blue-500 hover:text-blue-600 dark:text-blue-300 dark:hover:text-blue-400 transition-colors">
													{t("viewDetails")} →
												</Link>
											</td>
										</tr>
									))}
								</tbody>
								<tfoot className="bg-gray-50 dark:bg-gray-800">
									<tr>
										<td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
											Total
										</td>
										<td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-500 dark:text-gray-300">
											{projectData.reduce((total, project) => total + parseInt(project.realHours), 0)}h
										</td>
										<td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-500 dark:text-gray-300">
											{projectData.reduce((total, project) => total + parseInt(project.validatedHours), 0)}h
										</td>
										<td colSpan="2"></td>
									</tr>
									
								</tfoot>
							</table>
						</div>
					</div>

					{/* Mobile view (cards) */}
					<motion.div
						className="md:hidden space-y-4"
						variants={containerVariants}
						initial="hidden"
						whileInView="visible"
						viewport={{ once: true }}>
						{projectData.map((project) => (
							<motion.div
								key={project.id}
								variants={itemVariants}
								className="bg-white dark:bg-gray-700 rounded-lg shadow-md p-4">
								<div className="flex justify-between items-start mb-4">
									<h4 className="font-semibold text-gray-900 dark:text-white">
										{project.title}
									</h4>
									<Link
										to={project.link}
										className="text-blue-500 hover:text-blue-600 dark:text-blue-300 dark:hover:text-blue-400 transition-colors text-sm">
										{t("viewDetails")} →
									</Link>
								</div>
								<div className="grid grid-cols-2 gap-4 text-sm">
									<div>
										<p className="text-xs text-gray-500 dark:text-gray-400">
											{t("educationTableHeaders.realHours")}
										</p>
										<p className="text-gray-800 dark:text-gray-200">
											{project.realHours}h
										</p>
									</div>
									<div>
										<p className="text-xs text-gray-500 dark:text-gray-400">
											{t("educationTableHeaders.validatedHours")}
										</p>
										<p className="text-gray-800 dark:text-gray-200">
											{project.validatedHours}h
										</p>
									</div>
									<div>
										<p className="text-xs text-gray-500 dark:text-gray-400">
											{t("educationTableHeaders.theme")}
										</p>
										<p className="text-gray-800 dark:text-gray-200">
											{project.skills.split(',')[0]}
										</p>
									</div>
								</div>
							</motion.div>
						))}
						{/* Mobile totals card */}
						<motion.div
							variants={itemVariants}
							className="bg-gray-50 dark:bg-gray-800 rounded-lg shadow-md p-4">
							<h4 className="font-semibold text-gray-900 dark:text-white mb-2">
								{t("totalHours")}
							</h4>
							<div className="grid grid-cols-2 gap-4 text-sm">
								<div>
									<p className="text-xs text-gray-500 dark:text-gray-400">
										{t("educationTableHeaders.realHours")}
									</p>
									<p className="text-gray-800 dark:text-gray-200">
										{projectData.reduce((total, project) => total + parseInt(project.realHours), 0)}h
									</p>
								</div>
								<div>
									<p className="text-xs text-gray-500 dark:text-gray-400">
										{t("educationTableHeaders.validatedHours")}
									</p>
									<p className="text-gray-800 dark:text-gray-200">
										{projectData.reduce((total, project) => total + parseInt(project.validatedHours), 0)}h
									</p>
								</div>
							</div>
						</motion.div>
					</motion.div>
				</div>
			</div>
		</section>
	);
};

export default Education;
